<template>
  <v-container v-if="item.penalty === penaltyCategories.storeGroup" fluid>
    <v-row>
      <div class="field-heading pr-3 pt-2">
        {{ $t('settings.penaltyRules.storeGroupSelectText') }}:
      </div>
      <div class="pr-2">
        <v-select
          :value="selectedStoreGroupIndex"
          class="pa-0 ma-0"
          :items="storeGroupDropdownValues"
          :disabled="isTabDisabled(item)"
          @input="onSelectedStoreGroupIndexChange"
        >
          <template v-slot:item="props">
            <v-layout>
              <v-layout align-self-center justify-start>
                <span class="item-text">{{ props.item.text }}</span>
              </v-layout>
              <v-layout align-self-center justify-end>
                <v-icon v-if="isStoreGroupActive(props.item.value)" right>
                  done
                </v-icon>
              </v-layout>
            </v-layout>
          </template>
        </v-select>
      </div>
      <div>
        <v-checkbox
          v-model="isSelectedStoreGroupActive"
          class="pa-0 ma-0"
          :label="$t('settings.penaltyRules.active')"
          :disabled="isTabDisabled(item)"
        />
      </div>
      <!-- Exact Match Feature -->
      <div>
        <v-checkbox
          v-if="displayStoreGroupExactMatch"
          v-model="isSelectedStoreGroupExactMatch"
          class="pl-5 pt-0 pr-0 pb-0 ma-0"
          :label="$t('settings.penaltyRules.exactMatch')"
          :disabled="isTabDisabled(item)"
        />
      </div>
      <!-- Max Up/Down Feature -->
      <div>
        <v-checkbox
          v-if="displayStoreGroupMaxUpDown"
          v-model="isSelectedStoreGroupMaxUpDownEnabled"
          class="pl-5 pt-0 pr-0 pb-0 ma-0"
          :label="$t('settings.penaltyRules.storeGroupMaxUpDown.maxUpDownCheckboxText')"
          :disabled="isTabDisabled(item)"
        />
      </div>
      <div v-if="displayStoreGroupPenaltyError" class="field-heading pl-3 pt-1 red--text">
        <span>{{ selectedStoreGroupPenalty.error }}</span>
      </div>
    </v-row>
  </v-container>
</template>

<script>
import { get, isEmpty, find } from 'lodash';
import { mapState } from 'vuex';
import penaltyCategories from '@enums/penalty-categories';

/**
 * Store Group Penalty Tab Component
 * Manages store group penalty selection and feature toggles (exact match, max up/down).
 * Max Up/Down configuration is rendered in the parent component.
 */
export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
    mutableRule: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    storeGroupDropdownValues: {
      type: Array,
      required: true,
    },
    selectedStoreGroupIndex: {
      default: null,
    },
  },

  data() {
    return {
      penaltyCategories,
    };
  },

  computed: {
    ...mapState('clientConfig', ['releaseFlags']),

    displayStoreGroupExactMatch() {
      return this.releaseFlags.enableStoreGroupExactMatch;
    },

    displayStoreGroupMaxUpDown() {
      return this.releaseFlags.enableStoreGroupMaxUpDown;
    },

    selectedStoreGroupPenalty() {
      return find(this.mutableRule.storeGroupPenalty.penalties, {
        toolStoreGroupKey: this.selectedStoreGroupIndex,
      });
    },

    isSelectedStoreGroupExactMatch: {
      get() {
        if (isEmpty(this.selectedStoreGroupPenalty)) return false;
        return get(this.selectedStoreGroupPenalty, 'exactMatch', false);
      },
      set(value) {
        if (isEmpty(this.selectedStoreGroupPenalty)) return;
        this.$set(this.selectedStoreGroupPenalty, 'exactMatch', value);
        // If enabling Exact Match, disable Max Up/Down
        if (value) {
          this.toggleStoreGroupMaxUpDown(false);
        }
      },
    },

    isSelectedStoreGroupActive: {
      get() {
        if (isEmpty(this.selectedStoreGroupPenalty)) return false;
        return !!this.selectedStoreGroupPenalty.isActive;
      },
      set(value) {
        if (isEmpty(this.selectedStoreGroupPenalty)) return;
        this.$set(this.selectedStoreGroupPenalty, 'isActive', value);
        if (value) {
          this.deactivateStoreGroupPenalties();
        }
      },
    },

    isSelectedStoreGroupMaxUpDownEnabled: {
      get() {
        if (isEmpty(this.selectedStoreGroupPenalty)) return false;
        return get(this.selectedStoreGroupPenalty, 'maxUpDown.isActive', false);
      },
      set(value) {
        this.toggleStoreGroupMaxUpDown(value);
      },
    },

    displayStoreGroupPenaltyError() {
      return (
        this.isSelectedStoreGroupActive &&
        this.selectedStoreGroupPenalty &&
        this.selectedStoreGroupPenalty.error
      );
    },
  },

  methods: {
    isTabDisabled(tab) {
      return this.disabled || !tab || tab.disabled;
    },

    isStoreGroupActive(storeGroupKey) {
      const penalty = find(this.mutableRule.storeGroupPenalty.penalties, {
        toolStoreGroupKey: storeGroupKey,
      });
      return penalty ? penalty.isActive : false;
    },

    toggleStoreGroupMaxUpDown(enabled) {
      if (isEmpty(this.selectedStoreGroupPenalty)) return;
      this.$set(this.selectedStoreGroupPenalty, 'maxUpDown', {
        isActive: enabled,
        offsetType: 'pct',
        applyMaxUp: false,
        maxUp: null,
        applyMaxDown: false,
        maxDown: null,
      });
      if (enabled) {
        this.$set(this.selectedStoreGroupPenalty, 'exactMatch', false);
      }
    },

    deactivateStoreGroupPenalties() {
      const nonSelectedPenalties = this.mutableRule.storeGroupPenalty.penalties.filter(
        penalty =>
          penalty.toolStoreGroupKey && penalty.toolStoreGroupKey !== this.selectedStoreGroupIndex
      );
      if (isEmpty(nonSelectedPenalties)) return;
      nonSelectedPenalties.forEach(penalty => {
        this.$set(penalty, 'isActive', false);
      });
    },

    onSelectedStoreGroupIndexChange(value) {
      this.$emit('update:selectedStoreGroupIndex', value);
    },
  },
};
</script>

<style lang="scss" scoped>
.field-heading {
  font-weight: 500;
  word-break: break-word;
}

.caption {
  line-height: 1.4;
}
</style>
